/**
 * TunnelMap — underground tunnel network derived from cosmology-tunnels.png.
 *
 * The tunnel map is a 73×111 pixel image aligned to the cosmology sprite.
 * Magenta pixels (that are NOT ouroboros boundary) mark tunnel nodes.
 * Adjacent tunnel nodes (orthogonal) are connected by corridors that extend
 * continuously through the node center (no separate hollow room):
 *   - Horizontal corridor: 40×8+ tiles (curved) between adjacent nodes
 *   - Vertical corridor: 8+×40 tiles (curved) between adjacent nodes
 *
 * Corridor shape varies by cosmology zone:
 *   - Dungeon towers: perfectly straight corridors (no sin wave)
 *   - Underworld/hell layers: asymmetric waves (amplitude 6 and 2)
 *   - Elsewhere: symmetric waves (amplitude ±2)
 */

import {
    COSMOLOGY_WIDTH, COSMOLOGY_HEIGHT, PIXELS_TO_TILES,
    CENTER_PX, CENTER_PY, TILE_GRID_ORIGIN_Y,
    getZoneAtPixel, CosmologyZone, isUnderworldZone,
} from './CosmologyMap';

// ── Tunnel Node Data ─────────────────────────────────────────────────
// 73×111 = 8,103 bytes encoded as base64. Each byte is 1 (tunnel node) or 0.
// Data is encoded directly from cosmology-tunnels.png, aligned to cosmology.png.
// Magenta pixels in tunnels PNG that aren't ouroboros in cosmology PNG = tunnel nodes.

const TUNNEL_DATA_B64 =
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAABAAAAAQAAAAAAAQ' +
'AAAAAAAAAAAAAAAAAAAAEAAAAAAAEAAAABAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAQAAAAE' +
'AAAEAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAABAAAAAQAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAEA' +
'AAEAAAABAAABAAABAAAAAAAAAAEAAAAAAAAAAQAAAQAAAQAAAAEAAAEAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AABAAABAAABAAAAAQAAAQAAAQAAAAAAAAEBAQAAAAAAAAEAAAEAAAEAAAABAAABAAABAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAQAAAQAAAQAAAAEAAAEAAAEAAAAAAAAAAQAAAAAAAAABAAABAAABAAAAAQAAAQAAAQAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAEBAQEBAQEAAAABAQEBAQEBAAAAAAAAAAEAAAAAAAAAAQEBAQEBAQAAAAEBAQEBAQEAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAQAAAAAAAAAAAAABAAAAAAAAAAAAAAEAAAAAAAAAAAABA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQEAAAAAAAAAAAEAAAAAAAAAAAAAAQAAAAAAAAAAAAABAQAAAA' +
'AAAAAAAQEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAABAAAAAAAAAAAAAAEAAAAAAAAAAAA' +
'AAQAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAQAAAAABAAAAAAABAAAA' +
'AAABAAAAAAEAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAEAAAABAQEBA' +
'QEBAQEBAQEBAQEAAAABAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAABAA' +
'AAAAEAAAAAAAEAAAAAAAEAAAAAAQAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEBAQAAAAA' +
'AAAAAAQAAAAABAAAAAAABAAAAAAABAAAAAAEAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAB' +
'AAEAAAAAAAAAAAEAAAAAAQAAAAAAAQAAAAAAAQAAAAEBAQAAAAAAAAABAQEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAQABAAAAAAAAAAABAAAAAAAAAAAAAAEAAAAAAAAAAAABAAEAAAAAAAAAAQABAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAEAAQAAAAAAAAAAAQAAAAAAAAAAAQEBAQEAAAAAAAAAAQABAAAAAAAAAAEAAQAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAABAAEAAAAAAAAAAAEAAAAAAAAAAAAAAQAAAAAAAAAAAAEAAQAAAAAAAAABAAEAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAQABAAAAAQAAAAABAAAAAAEAAAAAAAEAAAAAAAEAAAABAAEAAAABAAAAA' +
'QABAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAQAAAQEAAQEAAAEAAAEBAQEBAAABAAAAAAABAAAAAAABAAAAAQABAA' +
'AAAQAAAQEAAQEAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAQEBAAEAAAABAAEBAQAAAAEAAAABAQEAAAEBAQEBAAABAQE' +
'AAAEAAQAAAQEBAAEAAAABAAEBAQAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAQAAAAAAAAAAAAEAAAAAAAEA' +
'AAAAAAEAAAAAAAAAAAABAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAEAAAAAAAAAAAABA' +
'AAAAAABAAAAAAABAAAAAAAAAAAAAQAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAABAAAAAA' +
'AAAAAAAQAAAAAAAQAAAAAAAQAAAAAAAAAAAAEAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAEBAAAAAAABAAAAAAAAAAA' +
'AAQAAAAAAAAAAAAEAAAAAAAEAAAAAAAEAAAAAAAAAAAABAAAAAAAAAAAAAQAAAAAAAQEAAAAAAAAAAQAAAAAAAQAA' +
'AAAAAAAAAAEAAAAAAAAAAAABAAAAAAABAAAAAAABAAAAAAAAAAAAAQAAAAAAAAAAAAEAAAAAAAEAAAAAAAAAAAEAA' +
'AAAAAEAAAAAAAAAAAABAAAAAAAAAAAAAQAAAAAAAQAAAAAAAQAAAAAAAAAAAAEAAAAAAAAAAAABAAAAAAABAAAAAA' +
'AAAAABAAAAAAABAAAAAAAAAAAAAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAAAAAAAAAAAAAQAAAAA' +
'AAQAAAAAAAAAAAQAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAABAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAA' +
'AAEAAAAAAAEAAAAAAAAAAAEAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAQAAAAAAAAEAAAAAAAAAAAAAA' +
'AAAAAAAAAABAAAAAAABAAAAAAAAAAABAAAAAAABAQEBAQEBAQEBAQEBAQEBAQEBAQAAAAAAAAEAAAAAAAABAQEBAQ' +
'EBAQEBAQEBAQEBAQEBAQAAAAAAAQAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAABAQEBAQEBAAA' +
'AAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAQEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAQAA' +
'AQAAAQAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAQAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQEBAQEBAQEAA' +
'AAAAAEAAAEAAAEAAAAAAAEBAQEBAQEBAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAA' +
'AAAAAAAAABAQEBAAABAQABAQEBAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAEAAAAAAAAAAAAAAAA' +
'AAAABAAAAAAAAAAAAAQAAAQAAAQAAAQAAAQAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAABAAAAAAAA' +
'AAAAAAAAAAAAAQAAAAAAAAAAAAEAAAEAAAEAAAEAAAEAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAA' +
'QAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAABAAABAAABAAABAAABAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAA' +
'AAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAQAAAAAAAQAAAAAAAQAAAAAAAAAAAAEAAAAAAAAAAAAAAAA' +
'AAAABAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAEAAAAAAAEAAAAAAAEAAAAAAAAAAAABAAAAAAAA' +
'AAAAAAAAAAAAAQAAAAAAAAABAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBA' +
'QEBAQEBAQEBAQEBAQEBAQEBAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAQAAAAAAAQAAAA' +
'AAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAEAAAEBAQEBAQE' +
'AAAEAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAB' +
'AAABAAABAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEBAQEBAQEBAQEBAQEBA' +
'QEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQ' +
'AAAAAAAAAAAAAAAAEAAAEAAAEAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAEAAAAAAAAAAAAAAAABAAABAAABAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAABAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQ' +
'EBAQEBAQEBAQEBAQEBAQEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQEBAQEBAQEBAQEBAQEBAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';

// ── Decoded tunnel data (lazily initialized) ────────────────────────

let _tunnelData: Uint8Array | null = null;

function decodeTunnelData(): Uint8Array {
    if (_tunnelData) return _tunnelData;
    const raw = atob(TUNNEL_DATA_B64);
    _tunnelData = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) {
        _tunnelData[i] = raw.charCodeAt(i);
    }
    return _tunnelData;
}

declare function atob(data: string): string;

// ── Tunnel Geometry Constants ────────────────────────────────────────

/** Offset within a 40×40 pixel area where the corridor center band starts. */
const HOLLOW_START = 16;
/** Offset within a 40×40 pixel area where the corridor center band ends. */
const HOLLOW_END = 23;
/** Default sin wave displacement (tiles) for normal corridors. */
const CORRIDOR_WAVE_AMPLITUDE = 2;
/** t ranges [0, CORRIDOR_T_MAX] across a full corridor (two halves). */
const CORRIDOR_T_MAX = 39;

// ── Deterministic Hash ──────────────────────────────────────────────

/**
 * Simple deterministic hash combining seed with corridor identity and wall side.
 * Returns a value in [0, 1) used to derive sin wave phase and period.
 */
function corridorHash(seed: number, px: number, py: number, dir: number, wall: number): number {
    // Knuth multiplicative hash — mixes bits well for small integers
    let h = (seed * 2654435761 + px * 2246822519 + py * 3266489917 + dir * 668265263 + wall * 374761393) | 0;
    h = ((h ^ (h >>> 16)) * 0x45d9f3b) | 0;
    h = (h ^ (h >>> 16)) | 0;
    return (h & 0x7FFFFFFF) / 0x7FFFFFFF;
}

/**
 * Get the wave amplitudes for the two walls of a corridor based on the
 * cosmology zone at the given pixel. Dungeon zones get straight corridors
 * (amplitude 0). Underworld/hell zones get asymmetric wide corridors
 * (amplitude 6 and 2). Normal zones get symmetric ±2.
 */
function getCorridorAmplitudes(px: number, py: number): [number, number] {
    const zone = getZoneAtPixel(px, py);
    if (zone === CosmologyZone.DUNGEON_TOWER) return [0, 0];
    if (isUnderworldZone(zone)) return [6, 2];
    return [CORRIDOR_WAVE_AMPLITUDE, CORRIDOR_WAVE_AMPLITUDE];
}

/**
 * Compute the sin-wave offset for one wall of a corridor at position `t` (0–39).
 * Each wall of each corridor gets its own deterministic phase and period,
 * so opposite walls undulate independently (corridor width varies organically).
 * The wave is pinched to zero at both endpoints (t=0 and t=39)
 * so corridors taper naturally at their ends.
 *
 * @param seed      World seed
 * @param px        Pixel X of the left/top node of this corridor
 * @param py        Pixel Y of the left/top node of this corridor
 * @param isHoriz   true for horizontal corridors, false for vertical
 * @param wall      0 = start wall (top/left edge), 1 = end wall (bottom/right edge)
 * @param t         Position along corridor 0–39 (left→right or top→bottom)
 * @param amplitude Maximum wave displacement for this wall
 */
function corridorWaveOffset(seed: number, px: number, py: number, isHoriz: boolean, wall: number, t: number, amplitude: number): number {
    if (amplitude === 0) return 0;
    const dir = isHoriz ? 0 : 1;
    const h = corridorHash(seed, px, py, dir, wall);
    // Period varies from 10 to 20 tiles per full wave
    const period = 10 + h * 10;
    // Phase offset unique to this corridor wall
    const phase = h * Math.PI * 2;
    // Envelope: sin(π * t/39) goes 0→1→0 across the corridor, ensuring
    // the wave vanishes at both ends where corridors taper
    const envelope = Math.sin(Math.PI * t / CORRIDOR_T_MAX);
    return Math.round(amplitude * envelope * Math.sin(2 * Math.PI * t / period + phase));
}

// ── Lookup Functions ─────────────────────────────────────────────────

/**
 * Returns true if the cosmology pixel at (px, py) is a tunnel node.
 */
export function isTunnelNode(px: number, py: number): boolean {
    if (px < 0 || px >= COSMOLOGY_WIDTH || py < 0 || py >= COSMOLOGY_HEIGHT) {
        return false;
    }
    return decodeTunnelData()[py * COSMOLOGY_WIDTH + px] === 1;
}

/**
 * Returns true if the given game tile should be cleared (set to SKY)
 * because it falls within a tunnel corridor.
 *
 * Corridors extend continuously through node centers (no separate hollow
 * room). Wave shape depends on cosmology zone: straight in dungeons,
 * asymmetric (amplitude 6/2) in underworld, symmetric ±2 elsewhere.
 *
 * Tunnel geometry per cosmology pixel (40×40 tiles):
 *   - Horizontal corridor to adjacent node: extends [16,39]+[0,23] (curved)
 *   - Vertical corridor to adjacent node: extends [16,39]+[0,23] (curved)
 */
export function isTunnelArea(tileX: number, tileY: number, seed: number): boolean {
    // Convert tile to cosmology pixel
    const px = Math.floor(tileX / PIXELS_TO_TILES) + CENTER_PX;
    const adjustedY = tileY - TILE_GRID_ORIGIN_Y;
    const py = Math.floor(adjustedY / PIXELS_TO_TILES) + CENTER_PY;

    // Offset within the 40×40 pixel area (handles negative coords correctly)
    const ox = ((tileX % PIXELS_TO_TILES) + PIXELS_TO_TILES) % PIXELS_TO_TILES;
    const oy = ((adjustedY % PIXELS_TO_TILES) + PIXELS_TO_TILES) % PIXELS_TO_TILES;

    // Zone-dependent wave amplitudes for this pixel
    const [amp0, amp1] = getCorridorAmplitudes(px, py);

    // 1. Horizontal corridors: connect adjacent horizontal tunnel nodes
    //    Each wall (top/bottom) undulates independently via its own sin wave.
    //    Corridors extend through the node center — no separate hollow room.
    {
        // Right half: offsets [16,39] — from this node's center through to the right edge
        if (ox >= HOLLOW_START && isTunnelNode(px, py) && isTunnelNode(px + 1, py)) {
            const t = ox - HOLLOW_START;  // 0–23 (first half of corridor)
            const topShift = corridorWaveOffset(seed, px, py, true, 0, t, amp0);
            const botShift = corridorWaveOffset(seed, px, py, true, 1, t, amp1);
            if (oy >= HOLLOW_START + topShift && oy <= HOLLOW_END + botShift) {
                return true;
            }
        }
        // Left half: offsets [0,23] — from the left edge through to this node's center
        if (ox <= HOLLOW_END && isTunnelNode(px - 1, py) && isTunnelNode(px, py)) {
            const t = ox + HOLLOW_START;  // 16–39 (second half of corridor)
            const topShift = corridorWaveOffset(seed, px - 1, py, true, 0, t, amp0);
            const botShift = corridorWaveOffset(seed, px - 1, py, true, 1, t, amp1);
            if (oy >= HOLLOW_START + topShift && oy <= HOLLOW_END + botShift) {
                return true;
            }
        }
    }

    // 2. Vertical corridors: connect adjacent vertical tunnel nodes
    //    Each wall (left/right) undulates independently via its own sin wave.
    //    Corridors extend through the node center — no separate hollow room.
    {
        // Bottom half: offsets [16,39] — from this node's center through to the bottom edge
        if (oy >= HOLLOW_START && isTunnelNode(px, py) && isTunnelNode(px, py + 1)) {
            const t = oy - HOLLOW_START;  // 0–23 (first half of corridor)
            const leftShift = corridorWaveOffset(seed, px, py, false, 0, t, amp0);
            const rightShift = corridorWaveOffset(seed, px, py, false, 1, t, amp1);
            if (ox >= HOLLOW_START + leftShift && ox <= HOLLOW_END + rightShift) {
                return true;
            }
        }
        // Top half: offsets [0,23] — from the top edge through to this node's center
        if (oy <= HOLLOW_END && isTunnelNode(px, py - 1) && isTunnelNode(px, py)) {
            const t = oy + HOLLOW_START;  // 16–39 (second half of corridor)
            const leftShift = corridorWaveOffset(seed, px, py - 1, false, 0, t, amp0);
            const rightShift = corridorWaveOffset(seed, px, py - 1, false, 1, t, amp1);
            if (ox >= HOLLOW_START + leftShift && ox <= HOLLOW_END + rightShift) {
                return true;
            }
        }
    }

    return false;
}
